import { IconButton, InputAdornment, FormControl, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { FieldError } from 'react-hook-form/dist/types';
import Spacing from '../spacing/spacing';
import useCalendarPickerStyles from './calendar-picker.styles';
import { withFormError, withFormRef } from '../form-integration';
import ErrorMessage from '../error/error-message';
import CONSTANTS from '../constants';
import { CalendarPickerView } from '@mui/lab/CalendarPicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { PopoverTooltip } from '../tooltip/tooltip';
import HelpIcon from '@material-ui/icons/Help';
import InputLabel from '../input-label/input-label';

interface CalendarPickerProps {
    name: string;
    value: string;
    label: string;
    fieldDescription?: string;
    required: boolean;
    minDate?: Date;
    maxDate?: Date;
    error?: FieldError;
    hasError?: boolean;
    views?: Array<CalendarPickerView>;
    openTo?: CalendarPickerView;
    ref?: React.Ref<HTMLElement>;
    innerRef?: any;
    inputFormat?: string;
    setValue?: any;
    setError?: any;
    noteLabel?: string;
    maxDateMessage?: string;
    reqValidationMessage?: string;
    minDateMessage?: string;
    noPlaceHolder?: boolean;
    unregister?: any;
    errorMessage?: string;
    onChange?: (event: Date) => void;
}

const CustomizedTextField = styled(TextField)`
        & .MuiOutlinedInput-root {
            borderRadius: '4px !important;
            border-color: white;
        }
        & .MuiInputBase-input {
            padding: 8px;
            padding-right: 0;
            border-color: white;
        }
        & .Mui-error {
            border-color: red;
        }
    `;

const CalendarPicker: React.FC<CalendarPickerProps> = ({
    name,
    value,
    label,
    fieldDescription,
    required,
    minDate,
    maxDate,
    hasError,
    errorMessage,
    views = ['year', 'month', 'day'],
    openTo = 'year',
    noPlaceHolder = false,
    onChange,
    setValue,
    setError,
    ref,
    error,
    innerRef,
    inputFormat,
    ...props
}) => {
    const formatDate = (date) => {
        return  moment(date).format(CONSTANTS.DATE_FORMAT_ISO_8601_WITH_HOURS_MIN_SEC);
    };
    const [selectedValue, setSelectedValue] = useState<string>(value);
    const [open, setOpen] = useState<boolean>(false);

    const classes = useCalendarPickerStyles();
    useEffect(() => {
        // If not set and when user clicks the edit option on an existing party
        // and then if user clicks submit, validation errors are still showing
        setValue(name, value ? formatDate(value) : null);
        setSelectedValue(value ? formatDate(value) : null);
    }, [name, value]);

    const handleChange = (item: Date): void => {
        setSelectedValue(formatDate(item));
        setValue(name, formatDate(item), { shouldValidate: true });
        if (onChange) {
            onChange(item);
        }
    };

    /**
     * Event handler for click on the date field or the calendar icon. 
     */
    const handleClick = () => {
        setValue(name, selectedValue, { shouldValidate: true });
    };

    const toggleCalendar = () => {
        setOpen(!open);
    }

    const closeCalendar = () => {
        setOpen(false);
    }

    return (
        <FormControl fullWidth className={classes.root}>
            <Spacing variable="spacing-base" />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    data-cy={name + 'input-calendar-picker'}
                    inputFormat={(inputFormat)}
                    renderInput={(params) =>
                        <CustomizedTextField
                            variant="outlined"
                            error={hasError}
                            name={name}
                            {...params}
                            label={
                                <InputLabel
                                    fieldName={label}
                                    isRequired={required}
                                    hasError={hasError}
                                />
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" className={classes.endAdornmentWrapper}>
                                        {fieldDescription && <div className={classes.helpTooltipAdornment}><PopoverTooltip messageDescription={fieldDescription}><HelpIcon /></PopoverTooltip></div>}
                                        <IconButton onClick={toggleCalendar} className={classes.calendar_icon}>
                                            <CalendarTodayIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    }
                    views={views}
                    open={open}
                    onClose={closeCalendar}
                    openTo={openTo}
                    value={selectedValue || null}
                    PopperProps={{
                        placement: "bottom-end",
                        className: classes.popover
                    }}
                    onChange={handleChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    {...(innerRef && { inputRef: innerRef })}
                    onOpen={handleClick}
                />
            </LocalizationProvider>
            <div className={classes.error__container}>
                {hasError && <ErrorMessage message={errorMessage} />}
            </div>
        </FormControl>
    );
};

export default withFormRef(withFormError(CalendarPicker)) as React.FC<CalendarPickerProps>;